import { useDebounceFn } from '@vueuse/core'

import type { ContestMechanic } from '@/types/global'
import type { Contest } from '@/types/types'

export type ContestPreviewMerchant = {
  id: number
  name: string
  logo: string
  webAppUrl: string
}

export type ContestPreviewPayload = {
  contest: Partial<Contest> & Record<string, unknown>
  mechanic?: ContestMechanic
  boostSlug?: string
  merchant?: ContestPreviewMerchant
  storeName?: string
}

export const toEmbeddableImage = async (path?: string | null): Promise<string | null> => {
  if (!path) return null
  if (!path.startsWith('blob:')) return path

  try {
    const response = await fetch(path)
    const blob = await response.blob()

    return await new Promise<string>((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.readAsDataURL(blob)
    })
  } catch {
    return path
  }
}

export const useContestPreviewFrame = (buildPayload: () => ContestPreviewPayload | Promise<ContestPreviewPayload>) => {
  const { locale } = useI18n()

  const contestsRootUrl = import.meta.env.VITE_CONTESTS_ROOT_URL
  const previewBaseUrl = computed(() => (contestsRootUrl ? new URL('/preview', contestsRootUrl) : null))
  const previewOrigin = computed(() => previewBaseUrl.value?.origin ?? '')
  const previewUrl = computed(() => {
    if (!previewBaseUrl.value) return ''

    const url = new URL(previewBaseUrl.value)
    url.searchParams.set('locale', locale.value)

    return url.toString()
  })

  const iframeRef = ref<HTMLIFrameElement>()
  let lastSendId = 0

  const sendPreviewData = async () => {
    if (!iframeRef.value?.contentWindow) return

    const sendId = ++lastSendId
    const payload = await buildPayload()

    if (sendId !== lastSendId || !iframeRef.value?.contentWindow) return

    iframeRef.value.contentWindow.postMessage(
      {
        type: 'PREVIEW_UPDATE',
        data: JSON.parse(JSON.stringify({ ...payload, locale: locale.value }))
      },
      previewOrigin.value
    )
  }

  const debouncedSendPreviewData = useDebounceFn(sendPreviewData, 200)

  const resetPreview = () => {
    iframeRef.value?.contentWindow?.postMessage({ type: 'PREVIEW_RESET' }, previewOrigin.value)
  }

  const handleMessage = (event: MessageEvent) => {
    if (!previewOrigin.value || event.origin !== previewOrigin.value) return
    if (event.data?.type === 'PREVIEW_READY') sendPreviewData()
  }

  onMounted(() => {
    window.addEventListener('message', handleMessage)
  })

  onUnmounted(() => {
    window.removeEventListener('message', handleMessage)
  })

  return {
    iframeRef,
    previewUrl,
    previewOrigin,
    sendPreviewData,
    debouncedSendPreviewData,
    resetPreview
  }
}
